import { useState, useEffect } from "react";
import { useAuth, useTheme, API } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  Table,
  CaretLeft,
  Sun,
  Moon,
  SignOut,
  Shield,
  ClockCounterClockwise,
  FileText,
  ArrowsClockwise,
  MagnifyingGlass,
  CaretDown,
  CaretUp,
  ArrowCounterClockwise,
  User,
  List,
  Warning,
  Eye,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

export default function AdminPage() {
  const { user, sessionId, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  // Logs state
  const [logFiles, setLogFiles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logSearchTerm, setLogSearchTerm] = useState("");
  const [logActionFilter, setLogActionFilter] = useState("ALLE");

  // Versions state
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (user && !isAdmin) {
      navigate("/");
    }
  }, [user, isAdmin, navigate]);

  // Fetch log files list
  const fetchLogFiles = async () => {
    try {
      setLogsLoading(true);
      const response = await axios.get(`${API}/admin/logs?session_id=${sessionId}`);
      setLogFiles(response.data || []);
    } catch (err) {
      console.error("Error fetching log files:", err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Fetch user logs
  const fetchUserLogs = async (username) => {
    try {
      setLogsLoading(true);
      const response = await axios.get(`${API}/admin/logs/${username}?session_id=${sessionId}&limit=500`);
      setUserLogs(response.data.entries || []);
      setSelectedUser(username);
    } catch (err) {
      console.error("Error fetching user logs:", err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Fetch versions
  const fetchVersions = async () => {
    try {
      setVersionsLoading(true);
      const response = await axios.get(`${API}/admin/versions?session_id=${sessionId}`);
      setVersions(response.data || []);
    } catch (err) {
      console.error("Error fetching versions:", err);
    } finally {
      setVersionsLoading(false);
    }
  };

  // Restore version
  const handleRestore = async () => {
    if (!versionToRestore) return;
    
    try {
      setRestoring(true);
      await axios.post(`${API}/admin/versions/${versionToRestore.version}/restore?session_id=${sessionId}`);
      setRestoreDialogOpen(false);
      setVersionToRestore(null);
      fetchVersions();
    } catch (err) {
      console.error("Error restoring version:", err);
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchLogFiles();
      fetchVersions();
    }
  }, [sessionId, isAdmin]);

  // Filter logs
  const filteredLogs = userLogs.filter((log) => {
    const matchesSearch = logSearchTerm === "" || 
      JSON.stringify(log).toLowerCase().includes(logSearchTerm.toLowerCase());
    const matchesAction = logActionFilter === "ALLE" || log.action === logActionFilter;
    return matchesSearch && matchesAction;
  });

  // Get unique actions from logs
  const uniqueActions = [...new Set(userLogs.map((log) => log.action))];

  // Format date
  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Action badge colors
  const getActionColor = (action) => {
    switch (action) {
      case "ADD_ENTRY": return "bg-green-500/10 text-green-700 dark:text-green-400";
      case "EDIT_ENTRY": return "bg-blue-500/10 text-blue-700 dark:text-blue-400";
      case "DELETE_ENTRY": return "bg-red-500/10 text-red-700 dark:text-red-400";
      case "EXPORT": return "bg-purple-500/10 text-purple-700 dark:text-purple-400";
      case "IMPORT_TABLE": return "bg-amber-500/10 text-amber-700 dark:text-amber-400";
      case "CLEAR_TABLE": return "bg-red-500/10 text-red-700 dark:text-red-400";
      case "RESTORE_VERSION": return "bg-cyan-500/10 text-cyan-700 dark:text-cyan-400";
      case "PASSWORD_CHANGE": return "bg-slate-500/10 text-slate-700 dark:text-slate-400";
      default: return "bg-gray-500/10 text-gray-700 dark:text-gray-400";
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="flex h-screen overflow-hidden noise-bg">
      {/* Sidebar - responsive */}
      <aside className="sidebar flex flex-col h-screen flex-shrink-0 w-16 md:w-56 transition-all duration-300">
        <div className="p-4 flex items-center justify-between">
          <h1 className="hidden md:block text-xl font-black uppercase tracking-tight">
            Admin
          </h1>
          <Shield className="md:hidden h-5 w-5 text-primary" weight="fill" />
        </div>

        <Separator />

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          <button
            data-testid="nav-dashboard"
            onClick={() => navigate("/")}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded text-foreground hover:bg-secondary transition-colors text-sm justify-center md:justify-start"
            title="Dashboard"
          >
            <Table className="h-4 w-4 flex-shrink-0" weight="bold" />
            <span className="hidden md:inline">Dashboard</span>
          </button>

          <button
            data-testid="nav-admin"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded bg-primary text-primary-foreground font-medium text-sm justify-center md:justify-start"
            title="Logs & Versionen"
          >
            <ClockCounterClockwise className="h-4 w-4 flex-shrink-0" weight="bold" />
            <span className="hidden md:inline">Logs & Versionen</span>
          </button>
        </nav>

        <Separator />

        {/* Bottom actions */}
        <div className="p-2 space-y-1">
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded text-foreground hover:bg-secondary transition-colors text-sm justify-center md:justify-start"
            title={theme === "dark" ? "Hell" : "Dunkel"}
          >
            {theme === "dark" ? (
              <>
                <Sun className="h-4 w-4 flex-shrink-0" weight="fill" />
                <span className="hidden md:inline">Hell</span>
              </>
            ) : (
              <>
                <Moon className="h-4 w-4 flex-shrink-0" weight="fill" />
                <span className="hidden md:inline">Dunkel</span>
              </>
            )}
          </button>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded text-destructive hover:bg-destructive/10 transition-colors text-sm justify-center md:justify-start"
            title="Abmelden"
          >
            <SignOut className="h-4 w-4 flex-shrink-0" />
            <span className="hidden md:inline">Abmelden</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden p-3 pr-4">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center gap-3 mb-3">
          <h2 className="text-base md:text-lg font-black uppercase tracking-tight">
            Logs & Versionen
          </h2>
          <Button
            data-testid="refresh-all-button"
            variant="ghost"
            size="sm"
            onClick={() => { fetchLogFiles(); fetchVersions(); }}
            className="h-8 w-8 p-0 ml-auto"
            title="Aktualisieren"
          >
            <ArrowsClockwise className={`h-4 w-4 ${logsLoading || versionsLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="logs" className="flex-1 flex flex-col min-h-0">
          <TabsList className="flex-shrink-0 w-fit">
            <TabsTrigger value="logs" data-testid="tab-logs" className="text-xs md:text-sm">
              <FileText className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Aktivitäts-</span>Logs
            </TabsTrigger>
            <TabsTrigger value="versions" data-testid="tab-versions" className="text-xs md:text-sm">
              <ClockCounterClockwise className="h-4 w-4 mr-1 md:mr-2" />
              Versionen
            </TabsTrigger>
          </TabsList>

          {/* Logs Tab */}
          <TabsContent value="logs" className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-3">
              {/* User list */}
              <div className="w-full md:w-48 flex-shrink-0 bg-card border border-border rounded overflow-hidden flex flex-col">
                <div className="p-2 border-b border-border bg-secondary/50">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Benutzer</p>
                </div>
                <div className="flex-1 overflow-auto p-1">
                  {logFiles.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-2">Keine Logs vorhanden</p>
                  ) : (
                    <div className="flex flex-row md:flex-col gap-1 flex-wrap">
                      {logFiles.map((logFile) => (
                        <button
                          key={logFile.username}
                          data-testid={`log-user-${logFile.username}`}
                          onClick={() => fetchUserLogs(logFile.username)}
                          className={`w-auto md:w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center gap-2 ${
                            selectedUser === logFile.username
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-secondary"
                          }`}
                        >
                          <User className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{logFile.username}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Log entries */}
              <div className="flex-1 min-h-0 bg-card border border-border rounded overflow-hidden flex flex-col">
                {selectedUser ? (
                  <>
                    {/* Filters */}
                    <div className="flex-shrink-0 p-2 border-b border-border bg-secondary/50 flex flex-wrap items-center gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-2">
                        Logs: {selectedUser}
                      </p>
                      <div className="flex-1" />
                      <div className="relative w-32 md:w-40">
                        <MagnifyingGlass className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                        <Input
                          data-testid="log-search-input"
                          type="text"
                          placeholder="Suche..."
                          value={logSearchTerm}
                          onChange={(e) => setLogSearchTerm(e.target.value)}
                          className="pl-6 h-6 text-xs"
                        />
                      </div>
                      <Select value={logActionFilter} onValueChange={setLogActionFilter}>
                        <SelectTrigger data-testid="log-action-filter" className="w-28 md:w-32 h-6 text-xs">
                          <SelectValue placeholder="Aktion" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ALLE">Alle Aktionen</SelectItem>
                          {uniqueActions.map((action) => (
                            <SelectItem key={action} value={action}>{action}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-[10px] text-muted-foreground">
                        {filteredLogs.length}/{userLogs.length}
                      </span>
                    </div>

                    {/* Log entries list */}
                    <div className="flex-1 overflow-auto">
                      {logsLoading ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
                        </div>
                      ) : filteredLogs.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                          Keine Log-Einträge gefunden
                        </div>
                      ) : (
                        <div className="divide-y divide-border">
                          {filteredLogs.map((log, idx) => (
                            <div key={idx} className="p-2 hover:bg-secondary/30 transition-colors">
                              <div className="flex items-start gap-2 flex-wrap">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getActionColor(log.action)}`}>
                                  {log.action}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {formatDate(log.timestamp)}
                                </span>
                              </div>
                              {log.details && Object.keys(log.details).length > 0 && (
                                <div className="mt-1 text-xs text-muted-foreground bg-secondary/30 rounded p-1.5 overflow-x-auto">
                                  <pre className="whitespace-pre-wrap break-words text-[10px]">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    <div className="text-center">
                      <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Wählen Sie einen Benutzer aus</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Versions Tab */}
          <TabsContent value="versions" className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 bg-card border border-border rounded overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0 p-2 border-b border-border bg-secondary/50 flex items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Tabellen-Versionen
                </p>
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {versions.length} Versionen
                </span>
              </div>

              {/* Versions list */}
              <div className="flex-1 overflow-auto">
                {versionsLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : versions.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    <div className="text-center">
                      <ClockCounterClockwise className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Keine Versionen vorhanden</p>
                    </div>
                  </div>
                ) : (
                  <table className="w-full data-table text-xs">
                    <thead className="bg-secondary sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-2 py-2 whitespace-nowrap">Zeitstempel</th>
                        <th className="text-left px-2 py-2 whitespace-nowrap hidden sm:table-cell">Benutzer</th>
                        <th className="text-left px-2 py-2 whitespace-nowrap hidden md:table-cell">Grund</th>
                        <th className="text-left px-2 py-2 whitespace-nowrap hidden lg:table-cell">Größe</th>
                        <th className="text-right px-2 py-2 whitespace-nowrap">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map((version, idx) => (
                        <tr key={version.version} className="border-t border-border hover:bg-secondary/30 transition-colors">
                          <td className="px-2 py-2 whitespace-nowrap">
                            <div>
                              <span className="block">{formatDate(version.created_at)}</span>
                              <span className="block sm:hidden text-[10px] text-muted-foreground">{version.changed_by}</span>
                            </div>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap hidden sm:table-cell">
                            <span className="inline-flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {version.changed_by}
                            </span>
                          </td>
                          <td className="px-2 py-2 hidden md:table-cell">
                            <span className="text-muted-foreground truncate max-w-[200px] block" title={version.reason}>
                              {version.reason}
                            </span>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap hidden lg:table-cell text-muted-foreground">
                            {formatSize(version.size_bytes || 0)}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-right">
                            <Button
                              data-testid={`restore-version-${idx}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setVersionToRestore(version);
                                setRestoreDialogOpen(true);
                              }}
                              className="h-6 px-2 text-xs"
                            >
                              <ArrowCounterClockwise className="h-3 w-3 mr-1" />
                              <span className="hidden sm:inline">Wiederherstellen</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warning className="h-5 w-5 text-amber-500" weight="fill" />
              Version wiederherstellen
            </DialogTitle>
            <DialogDescription>
              Möchten Sie diese Version wirklich wiederherstellen?
            </DialogDescription>
          </DialogHeader>
          
          {versionToRestore && (
            <div className="bg-secondary/50 rounded p-3 text-sm space-y-1">
              <p><strong>Zeitstempel:</strong> {formatDate(versionToRestore.created_at)}</p>
              <p><strong>Erstellt von:</strong> {versionToRestore.changed_by}</p>
              <p><strong>Grund:</strong> {versionToRestore.reason}</p>
            </div>
          )}
          
          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-700 dark:text-amber-400">
            <strong>Hinweis:</strong> Die aktuelle Tabelle wird automatisch als neue Version gesichert, bevor die Wiederherstellung erfolgt.
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRestoreDialogOpen(false)}
              disabled={restoring}
            >
              Abbrechen
            </Button>
            <Button
              data-testid="confirm-restore-button"
              onClick={handleRestore}
              disabled={restoring}
            >
              {restoring ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Wiederherstellen...
                </>
              ) : (
                <>
                  <ArrowCounterClockwise className="h-4 w-4 mr-2" />
                  Wiederherstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
